import type { BellPatternId } from "@retreat/core";
import { PATTERN_LIBRARY } from "@retreat/bells";
import { bellPatternLabel } from "./bellPatternLabels";

/** A single card on the Teaching screen — one bell signal and what it means. */
export type TeachingBell = {
  id: string;
  name: string;
  tradition: string;
  role: string;
  teaches: string;
};

/**
 * Teaching prose for each signal — "what does that bell mean?". This is the one
 * thing the pattern data itself does not carry: curated, instructional voice
 * (the pattern's own `description` is technical). Partial — patterns without
 * bespoke copy fall back to DEFAULT_BELL_MEANING.
 */
const BELL_PATTERN_MEANINGS: Partial<Record<BellPatternId, string>> = {
  // Zen
  zazen_deep_open:
    "A sitting period is beginning. A deep bell settles the room, then softer bells call you to your seat.",
  zazen_open: "A sitting period is beginning. Come to stillness.",
  zazen_close: "The sitting is ending. Move slowly, and stay in silence.",
  zazen_interval: "Midway through. A single bell to gather a wandering mind.",
  han_opening: "The retreat is beginning. Everything stops. Come to the cushion.",
  han_closing: "The retreat is complete.",
  kinhin_begin: "Walking meditation begins. Rise and form the line.",
  kinhin_interval: "Stay present in the walk. A small bell marks the breath.",
  kinhin_end: "Walking is ending. Return to your seat.",
  oryoki_meal_bell: "The meal begins. Eat in silence and gratitude.",
  mindfulness_bell: "Pause and breathe. Return to this moment.",
  inkin_single: "A small bell marks the moment. Stay present.",
  keisu_single: "A grounding tone. Settle.",
  rin_single: "A single bell. Notice, and return.",
  ogane_single: "A deep bell — a major moment in the day.",
  chant_begin: "Chanting opens. Find the rhythm.",
  chant_end: "Chanting closes. Let the sound fade into silence.",
  chant_mokugyo: "A steady wooden beat keeps the pace of the chant.",
  // Vipassana
  goenka_session_start: "The sitting begins. Close your eyes and start the work.",
  goenka_session_end: "The sitting is complete. Open gently.",
  goenka_adhitthana_bell:
    "A sitting of strong determination. Resolve to stay still.",
  vipassana_gong_single: "A single tone marks the boundary of the sit.",
};

const DEFAULT_BELL_MEANING = "A signal in the day. You'll come to know it.";

/** Teaching prose for a pattern, with a gentle fallback for uncurated signals. */
export function bellPatternMeaning(id: BellPatternId): string {
  return BELL_PATTERN_MEANINGS[id] ?? DEFAULT_BELL_MEANING;
}

/**
 * Short role tag shown as "{tradition} · {role}" (e.g. "Zen · opening").
 * Derived from the pattern's own ritual purpose — the single source of truth —
 * rather than a duplicated lookup, so it can never disagree with the pattern.
 */
export function bellPatternRole(id: BellPatternId): string {
  return PATTERN_LIBRARY[id].semantics.ritualPurpose;
}

/**
 * Builds the Teaching-screen cards from the retreat's *actual* bell patterns
 * (as returned by getRetreatBellPatterns), so "what does that bell mean?" always
 * matches what the retreat really plays — name, role, meaning, and (via the
 * patternId as id) the real sound behind the play button.
 *
 * Replaces the old hand-authored `schedule.bellMeanings`, which drifted out of
 * sync with the patterns (it labelled the opening a "single bell" after it
 * became ōgane + triple rin).
 */
export function deriveTeachingBells(
  patternIds: BellPatternId[],
  tradition: string,
): TeachingBell[] {
  return patternIds.map((patternId) => ({
    id: patternId,
    name: bellPatternLabel(patternId),
    tradition,
    role: bellPatternRole(patternId),
    teaches: bellPatternMeaning(patternId),
  }));
}
